import React from 'react';

// Maps an activeTab key to its human-readable tool name for the breadcrumb.
const TAB_LABELS = {
  'spec-check': 'Spec Check',
  'spec-builder': 'Spec Builder',
  'spec-update': 'Spec Update',
  'rfi-check': 'RFI Check',
  'shop-drawing-check': 'Package Check',
  'design-vs-shop': 'Design vs. Shop',
  'drawing-compare': 'Drawing Compare',
  'track-changes': 'Track Changes',
  'integrations': 'Integrations',
  'settings': 'Settings',
  'faq': 'Help & FAQ',
};

const GOLD = '#D4A417';
const BG = '#13161e';
const BORDER = '#252a38';
const FONT_STACK = "'Syne', sans-serif";

// Derive an org display name from an email's domain.
// e.g. mario@aecworks.ai -> AECWorks AI
function orgNameFromEmail(email) {
  if (!email || typeof email !== 'string' || !email.includes('@')) return '';
  const domain = email.split('@')[1] || '';
  const label = domain.split('.')[0] || '';
  if (!label) return '';
  return label.charAt(0).toUpperCase() + label.slice(1);
}

// Derive avatar initials from an email's local part.
// e.g. mario@aecworks.ai -> AECWorks AI
function initialsFromEmail(email) {
  if (!email || typeof email !== 'string') return '?';
  const local = email.split('@')[0] || '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function TopBar({ user, activeMode, onModeChange, activeTab, onLogout, tier }) {
  const resolvedTier = tier || 'Standard';
  const orgName = orgNameFromEmail(user && user.email);
  const initials = initialsFromEmail(user && user.email);
  const toolName = TAB_LABELS[activeTab] || '';

  const isManagerLocked = !tier ||
    (tier.toLowerCase() !== 'pro' && tier.toLowerCase() !== 'enterprise');

  const modeButtonStyle = (mode) => ({
    padding: '6px 14px',
    fontSize: '13px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'all 0.15s ease',
    background: activeMode === mode ? GOLD : 'transparent',
    color: activeMode === mode ? '#13161e' : '#9ca3af',
  });

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '52px',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        gap: '14px',
        background: '#13161e',
        borderBottom: '1px solid #252a38',
        fontFamily: 'sans-serif',
        boxSizing: 'border-box',
      }}
    >
      {/* Left: logo + breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ fontFamily: FONT_STACK, fontSize: '18px', fontWeight: '700', letterSpacing: '0.5px' }}>
          <span style={{ color: GOLD }}>AW</span>
          <span style={{ color: '#9ca3af' }}> AI Tools</span>
        </div>

        {toolName && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#3f4658', fontSize: '14px' }}>/</span>
            <span style={{ color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>{toolName}</span>
          </div>
        )}
      </div>

      {/* Center: mode toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px',
          background: '#0d0f15',
          border: `1px solid ${BORDER}`,
          borderRadius: '8px',
        }}
      >
        <button
          type="button"
          style={modeButtonStyle('traditional')}
          onClick={() => onModeChange && onModeChange('traditional')}
        >
          Traditional
        </button>
        <button
          type="button"
          style={{
            ...modeButtonStyle('manager'),
            opacity: isManagerLocked ? 0.6 : 1,
            cursor: isManagerLocked ? 'not-allowed' : 'pointer',
          }}
          onClick={() => {
            if (isManagerLocked) {
              alert('AECWorks AI Manager requires a Pro or Enterprise plan. Contact hello@aecworks.ai to upgrade.');
              return;
            }
            onModeChange && onModeChange('manager');
          }}
        >
          {isManagerLocked ? '🔒 ' : ''}Manager
        </button>
      </div>

      {/* Right: tier badge, org badge, avatar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, overflow: 'visible' }}>
        <span
          style={{
            padding: '4px 10px',
            fontSize: '11px',
            fontWeight: '600',
            color: GOLD,
            background: 'rgba(212, 164, 23, 0.12)',
            border: `1px solid ${GOLD}`,
            borderRadius: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {resolvedTier}
        </span>

        {orgName && (
          <span
            style={{
              padding: '4px 10px',
              fontSize: '12px',
              fontWeight: '500',
              color: '#9ca3af',
              background: '#1b1f2a',
              border: `1px solid ${BORDER}`,
              borderRadius: '12px',
            }}
          >
            {orgName}
          </span>
        )}

        <span style={{ fontSize: '11px', color: '#7a8099' }}>
          {(user && user.email) || ''}
        </span>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '34px',
            height: '34px',
            borderRadius: '50%',
            background: GOLD,
            color: '#13161e',
            fontSize: '13px',
            fontWeight: '700',
          }}
          title={(user && user.email) || ''}
        >
          {initials}
        </div>

        <button
          type="button"
          onClick={() => onLogout && onLogout()}
          style={{
            fontSize: '11px',
            padding: '4px 10px',
            borderRadius: '4px',
            border: '1px solid #2e3548',
            background: 'transparent',
            color: '#7a8099',
            cursor: 'pointer',
            fontFamily: 'sans-serif',
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

export default TopBar;
